import "./ClientBasicDetailsFormComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import {ImageConfig, Misc, Patterns} from "../../../../constants";
import FormikInputComponent from "../../../../shared/components/form-controls/formik-input/FormikInputComponent";
import CardComponent from "../../../../shared/components/card/CardComponent";
import ButtonComponent from "../../../../shared/components/button/ButtonComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import FormikDatePickerComponent
    from "../../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import IconButtonComponent from "../../../../shared/components/icon-button/IconButtonComponent";
import FormControlLabelComponent from "../../../../shared/components/form-control-label/FormControlLabelComponent";
import {IClientBasicDetails} from "../../../../shared/models/client.model";
import {getClientBasicDetails, setClientBasicDetailsSuccess} from "../../../../store/actions/client.action";
import LoaderComponent from "../../../../shared/components/loader/LoaderComponent";
import StatusCardComponent from "../../../../shared/components/status-card/StatusCardComponent";
import ToolTipComponent from "../../../../shared/components/tool-tip/ToolTipComponent";
import {useParams} from "react-router-dom";
import HorizontalLineComponent
    from "../../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";
import FormikPhoneInputComponent from "../../../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import DetailsViewCardComponent from "../../../../shared/components/details-view-card/DetailsViewCardComponent";
import {setFormChangesData} from "../../../../store/actions/form-state.action";
import HeadingWithNavigationComponent
    from "../../../../shared/components/heading-with-navigation/HeadingWithNavigationComponent";
import FormDebuggerComponent from "../../../../shared/components/form-debugger/FormDebuggerComponent";
import moment from "moment";
import ModalComponent from "../../../../shared/components/modal/ModalComponent";
import AcknowledgementFormComponent from "../../acknowledgement-form/AcknowledmentFormComponent";

interface ClientBasicDetailsFormComponentProps {
    onCancel: () => void;
    onSave: (clientBasicDetails: any) => void;
    clientBasicDetails: IClientBasicDetails;
    isClientBasicDetailsLoading: boolean | undefined;
    isClientBasicDetailsLoadingFailed: boolean | undefined;
}

const contactInfoSchema = Yup.object({
    phone_type: Yup.string().required('Phone type is required'),
    phone: Yup.string().required('Phone number is required'),
});

const EmailObj = {
    email: ""
}

const PhoneObj = {
    phone_type: "",
    phone: ""
}

const primaryEmailInfoSchema = Yup.object({
    email: Yup.string().email('Enter a valid email address')
        .max(100, 'Email cannot be more than 100 characters')
        .required('Email address is required'),
});


const ClientBasicDetailsFormValidationSchema = Yup.object({
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    is_alias_name_set: Yup.boolean(),
    alias_first_name: Yup.string().when('is_alias_name_set', {
        is: true,
        then: Yup.string().required('Alias First Name is required'),
    }),
    alias_last_name: Yup.string().when('is_alias_name_set', {
        is: true,
        then: Yup.string().required('Alias Last Name is required'),
    }),
    dob: Yup.mixed().required('Date of Birth is required'),
    ssn: Yup.string()
        // .required('SSN Number is required')
        .min(9, 'Enter valid SSN Number')
        .max(9, 'SSN cannot be more than 9-digits'),
    gender: Yup.string().required('Gender is required'),
    work_info: Yup.object({
        occupation: Yup.string().required('Occupation is required'),
        employment_status: Yup.string().required('Employment Status is required'),
    }),
    primary_email: Yup.string().email().required('Email is required'),
    primary_contact_info: Yup.object({
        phone_type: Yup.string().required('Phone Type is required'),
        phone: Yup.string()
            .required('Phone Number is required')
            .test('is-ten-digits', 'Phone number must contain exactly 10 digits', (value: any) => {
                return value?.length === 10
            }),
    }),
    secondary_emails: Yup.array().of(
        Yup.object().shape({
            email: Yup.string().email('Invalid email')
        })
    ),
    secondary_contact_info: Yup.array().of(
        Yup.object().shape({
            phone_type: Yup.string().required('Phone Type is required'),
            phone: Yup.string()
                .test('is-ten-digits', 'Phone number must contain exactly 10 digits', (value: any) => {
                    return value?.length === 10;
                }),
        })
    ),
    emergency_contact_info: Yup.object({
        primary_emergency: Yup.object({
            name: Yup.string().required('Full Name is required'),
            relationship: Yup.string().required('Relationship is required'),
            language: Yup.string().required('Language is required'),
            primary_contact_info: Yup.object({
                phone_type: Yup.string().required('Phone Type is required'),
                phone: Yup.string()
                    .required('Phone Number is required')
                    .test('is-ten-digits', 'Phone number must contain exactly 10 digits', (value: any) => {
                        return value?.length === 10
                    }),
            }),
            secondary_contact_info: Yup.array().of(
                Yup.object().shape({
                    phone: Yup.string()
                        .test('is-ten-digits', 'Phone number must contain exactly 10 digits', (value: any) => {// Remove non-digits
                            return value?.length === 10;
                        }).nullable(),
                })
            ),
        }),
        secondary_emergency: Yup.object().shape({
            primary_contact_info: Yup.object({
                phone: Yup.string()
                    .test('is-ten-digits', 'Phone number must contain exactly 10 digits', function (value) {
                        if (value) {
                            return value.length === 10;
                        }
                        return true; // Allow empty value
                    })
                    .notRequired(),
            }),
            secondary_contact_info: Yup.array().of(
                Yup.object().shape({
                    phone: Yup.string()
                        .test('is-ten-digits', 'Phone number must contain exactly 10 digits', function (value) {
                            if (value) {
                                return value.length === 10;
                            }
                            return true; // Allow empty value
                        })
                        .notRequired(),
                })
            ),
        }),

    }),

    address: Yup.object({
        address_line: Yup.string()
            .min(1, 'Address line is required')
            .max(100, 'Address line cannot be more than 100 characters')
            .required('Address Line is required'),
        city: Yup.string()
            .min(1, 'City is required')
            .max(100, 'City cannot be more than 100 characters')
            .required('City is required'),
        state: Yup.string()
            .min(1, 'State is required')
            .max(100, 'State cannot be more than 100 characters')
            .required('State is required'),
        country: Yup.string()
            .min(2, 'Country must be at least 2 characters')
            .max(100, 'Country cannot be more than 100 characters')
            .required('Country is required'),
        zip_code: Yup.string()
            .matches(/^[0-9]{5}$/, 'Enter a valid ZIP Code')
            .required('ZIP Code is required'),
    })
});

const ClientBasicDetailsFormInitialValues: IClientBasicDetails = {
    first_name: "",
    last_name: "",
    gender: "",
    dob: "",
    nick_name: "",
    ssn: "",
    show_secondary_emergency_form: false,
    primary_email: "",
    // secondary_emails: [EmailObj],
    primary_contact_info: PhoneObj,
    // secondary_contact_info: [PhoneObj],
    emergency_contact_info: {
        primary_emergency: {
            name: "",
            relationship: "",
            language: "",
            primary_contact_info: PhoneObj,
            // secondary_contact_info: [PhoneObj],
        },
        secondary_emergency: {
            name: "",
            relationship: "",
            language: "",
            primary_contact_info: PhoneObj,
            // secondary_contact_info: [PhoneObj],
        }
    },
    work_info: {
        occupation: "",
        employment_status: ""
    },
    address: {
        address_line: "",
        city: "",
        country: "",
        zip_code: "",
        state: ""
    },
    send_invite: false
};


const ClientBasicDetailsFormComponent = (props: ClientBasicDetailsFormComponentProps) => {

    const {
        onCancel,
        onSave,
        clientBasicDetails,
        isClientBasicDetailsLoading,
        isClientBasicDetailsLoadingFailed
    } = props;
    const {clientId} = useParams();
    const [clientBasicDetailsFormInitialValues, setClientBasicDetailsFormInitialValues] = useState<IClientBasicDetails>(_.cloneDeep(ClientBasicDetailsFormInitialValues));
    const [isClientBasicDetailsSavingInProgress, setIsClientBasicDetailsSavingInProgress] = useState(false);
    const dispatch = useDispatch();
    const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] = useState(false);

    const {
        genderList,
        isGenderListLoaded,
        isGenderListLoading,
        employmentStatusList,
        phoneTypeList,
        languageList,
        relationshipList
    } = useSelector((state: IRootReducerState) => state.staticData);


    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        setClientBasicDetailsFormInitialValues(values);
        setIsAcknowledgementModalOpen(true);
    }, []);

    const handleAcknowledgementFormSubmit = (acknowledgementValues: any) => {
        const payload = {
            ...CommonService.removeKeysFromJSON(_.cloneDeep(clientBasicDetailsFormInitialValues), ['language_details', 'phone_type_details', 'relationship_details', 'gender_details', 'employment_status_details']),
            ...acknowledgementValues
        };
        delete payload.appointment_id;
        payload['dob'] = moment(payload['dob']).format('YYYY-MM-DD');
        setIsClientBasicDetailsSavingInProgress(true);
        CommonService._client.ClientBasicDetailsSaveAPICall(payload)
            .then((response: IAPIResponseType<IClientBasicDetails>) => {
                CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
                setIsClientBasicDetailsSavingInProgress(false);
                dispatch(setClientBasicDetailsSuccess(response.data));
                setIsAcknowledgementModalOpen(false);
                onSave(response.data);
            }).catch((error: any) => {
            CommonService._alert.showToast(error[Misc.API_RESPONSE_MESSAGE_KEY], "error");
            setIsClientBasicDetailsSavingInProgress(false);
        });
    }

    const patchClientBasicDetails = useCallback(() => {
        if (clientBasicDetails) {
            const primaryEmergency = clientBasicDetails?.emergency_contact_info?.primary_emergency;
            if ((primaryEmergency?.secondary_contact_info?.every((contact: any) => contact.phone_type === "" && contact.phone === ""))) {
                clientBasicDetails.emergency_contact_info.primary_emergency.secondary_contact_info = undefined;
            }
            const tempSecondaryEmergency = clientBasicDetails?.emergency_contact_info?.secondary_emergency;
            if ((tempSecondaryEmergency?.secondary_contact_info?.every((contact: any) => contact.phone_type === "" && contact.phone === ""))) {
                clientBasicDetails.emergency_contact_info.secondary_emergency.secondary_contact_info = undefined;
            }
            if (tempSecondaryEmergency?.name || tempSecondaryEmergency?.language_details || tempSecondaryEmergency?.relationship || tempSecondaryEmergency?.primary_contact_info?.phone || tempSecondaryEmergency?.primary_contact_info?.phone_type || (tempSecondaryEmergency?.secondary_contact_info && tempSecondaryEmergency?.secondary_contact_info?.length > 0
                && tempSecondaryEmergency?.secondary_contact_info?.some((contact: any) => contact.phone_type !== "" || contact.phone !== ""))) {
                clientBasicDetails.show_secondary_emergency_form = true;
            } else {
                clientBasicDetails.show_secondary_emergency_form = false;
            }
            if (clientBasicDetails?.secondary_contact_info?.length === 0) {
                clientBasicDetails.secondary_contact_info = [{
                    phone: "",
                    phone_type: ""
                }];
            }
            if (clientBasicDetails?.secondary_emails?.length === 0) {
                clientBasicDetails.secondary_emails = [{
                    email: "",
                }];
            }
            if (!clientBasicDetails?.secondary_emails?.some((item: any) => item.email !== "")) clientBasicDetails.secondary_emails = undefined;
            if (!clientBasicDetails?.secondary_contact_info?.some((item: any) => item.phone !== "" || item.phone_type !== "")) clientBasicDetails.secondary_contact_info = undefined;
            if (!clientBasicDetails?.primary_email) {
                clientBasicDetails.primary_email = "";
            }
            if (!clientBasicDetails?.primary_contact_info) {
                clientBasicDetails.primary_contact_info = PhoneObj;
            }
            setClientBasicDetailsFormInitialValues(clientBasicDetails);
        }
    }, [clientBasicDetails])

    useEffect(() => {
        patchClientBasicDetails();
    }, [clientBasicDetails, patchClientBasicDetails]);

    useEffect(() => {
        if (clientId) {
            dispatch(getClientBasicDetails());
        }
    }, [clientId, dispatch]);


    return (
        <div className={'client-basic-details-form-component'}>
            <HeadingWithNavigationComponent onBackNavigation={onCancel}
                                            title={"Back"}/>
            <CardComponent title="Edit Profile Details">
                <>
                    {
                        isClientBasicDetailsLoading && <div>
                            <LoaderComponent/>
                        </div>
                    }
                    {
                        isClientBasicDetailsLoadingFailed &&
                        <StatusCardComponent title={"Failed to fetch client Details"}/>
                    }
                </>
                <Formik
                    validationSchema={ClientBasicDetailsFormValidationSchema}
                    initialValues={clientBasicDetailsFormInitialValues}
                    onSubmit={onSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    validateOnMount={true}>
                    {({values, touched, isValid, errors, setFieldValue, validateForm, dirty}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                            if (dirty) {
                                dispatch(setFormChangesData(true));
                            }
                        }, [validateForm, values, dirty]);
                        return (
                            <Form noValidate={true} className={"t-form"}>
                                <FormDebuggerComponent showDebugger={false} values={values} errors={errors}/>
                                <div className="t-form-controls">
                                    <DetailsViewCardComponent title={"Personal Details"}
                                                              className={'personal-details-heading'}>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'first_name'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'First Name'}
                                                                placeholder={'First Name'}
                                                                type={"text"}
                                                                required={true}
                                                                titleCase={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'last_name'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Last Name'}
                                                                placeholder={'Last Name'}
                                                                type={"text"}
                                                                required={true}
                                                                titleCase={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"/>
                                        </div>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'nick_name'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Nickname/Preferred Name'}
                                                                placeholder={'Nickname/Preferred Name'}
                                                                type={"text"}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'dob'} className="t-form-control">
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikDatePickerComponent
                                                                id={'dob_input'}
                                                                label={'Date of Birth'}
                                                                placeholder={'Select Date of Birth'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                // clearable={true}
                                                                maxDate={moment()}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'gender'} className="t-form-control">
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={'gender_dd'}
                                                                label={'Gender'}
                                                                options={genderList}
                                                                menuPlacement={"top"}
                                                                isDataLoading={isGenderListLoading}
                                                                isDataLoaded={isGenderListLoaded}
                                                                placeholder={'Select Gender'}
                                                                required={true}
                                                                isClearable={false}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'ssn'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                id={'ssn_input'}
                                                                label={'SSN (Social Security Number)'}
                                                                placeholder={'Enter SSN Number'}
                                                                maxlength={9}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                validationPattern={Patterns.POSITIVE_WHOLE_NUMBERS}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                    </DetailsViewCardComponent>
                                    <DetailsViewCardComponent title={"Contact Information"}>
                                        <FormControlLabelComponent label={'Primary Phone :'}/>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'primary_contact_info.phone_type'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={'phone_type_dd'}
                                                                options={phoneTypeList}
                                                                label={'Phone Type'}
                                                                placeholder={'Select Phone Type'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'primary_contact_info.phone'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikPhoneInputComponent
                                                                label={'Phone Number'}
                                                                disabled={!values.primary_contact_info.phone_type}
                                                                placeholder={'Enter Phone Number'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1 d-flex align-items-center">
                                                <IconButtonComponent className={"form-helper-icon"}>
                                                    <ToolTipComponent
                                                        showArrow={true}
                                                        position={"left"}
                                                        tooltip={"This phone number will be used to contact you if necessary. Please ensure it remains active and operational at all times."}>
                                                        <ImageConfig.InfoIcon/>
                                                    </ToolTipComponent>
                                                </IconButtonComponent>
                                            </div>
                                        </div>
                                        {
                                            values?.secondary_contact_info && values?.secondary_contact_info?.length > 0 ?
                                                <>
                                                    <FormControlLabelComponent label={'Alternate Phone'}/>
                                                    <FieldArray
                                                        name="secondary_contact_info"
                                                        render={(arrayHelpers) => (
                                                            <>
                                                                {values?.secondary_contact_info && values?.secondary_contact_info?.map((item: any, index: any) => {
                                                                    return (
                                                                        <div className="ts-row d-flex" key={index}>
                                                                            <div className="ts-col">
                                                                                <Field
                                                                                    name={`secondary_contact_info[${index}].phone_type`}>
                                                                                    {
                                                                                        (field: FieldProps) => (
                                                                                            <FormikSelectDropdownComponent
                                                                                                options={phoneTypeList}
                                                                                                label={'Phone Type'}
                                                                                                required={false}
                                                                                                isClearable={false}
                                                                                                placeholder={'Select Phone Type'}
                                                                                                formikField={field}
                                                                                                fullWidth={true}
                                                                                            />
                                                                                        )
                                                                                    }
                                                                                </Field>
                                                                            </div>
                                                                            <div className="ts-col">
                                                                                <Field
                                                                                    name={`secondary_contact_info[${index}].phone`}>
                                                                                    {
                                                                                        (field: FieldProps) => (
                                                                                            <FormikPhoneInputComponent
                                                                                                label={'Phone Number'}
                                                                                                required={false}
                                                                                                disabled={values.secondary_contact_info && !values.secondary_contact_info[index].phone_type}
                                                                                                placeholder={'Enter Phone Number'}
                                                                                                formikField={field}
                                                                                                fullWidth={true}
                                                                                            />
                                                                                        )
                                                                                    }
                                                                                </Field>
                                                                            </div>
                                                                            <div
                                                                                className="ts-col-md-1 d-flex align-items-center registration-form-icons-wrapper">

                                                                                <IconButtonComponent
                                                                                    className={"form-helper-icon"}
                                                                                    onClick={() => {
                                                                                        arrayHelpers.remove(index);
                                                                                    }}
                                                                                >
                                                                                    <ImageConfig.DeleteIcon/>
                                                                                </IconButtonComponent>

                                                                                {(values?.secondary_contact_info && index === values.secondary_contact_info.length - 1)
                                                                                    && values.secondary_contact_info.length < 3 &&
                                                                                    <IconButtonComponent
                                                                                        className={"form-helper-icon"}
                                                                                        disabled={!contactInfoSchema.isValidSync(values.secondary_contact_info[index])}
                                                                                        onClick={() => {
                                                                                            arrayHelpers.push(PhoneObj);
                                                                                        }}
                                                                                    >
                                                                                        <ImageConfig.AddCircleIcon/>
                                                                                    </IconButtonComponent>
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </>
                                                        )}/>
                                                </> :
                                                <ButtonComponent variant={"link"}
                                                                 color={"info"}
                                                                 className={'mrg-bottom-20'}
                                                                 size={"small"}
                                                                 prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                                 onClick={() => {
                                                                     setFieldValue('secondary_contact_info', [{
                                                                         phone_type: "",
                                                                         phone: ""
                                                                     }]);
                                                                 }}
                                                >
                                                    Add Alternate Phone
                                                </ButtonComponent>

                                        }
                                        <HorizontalLineComponent marginBottom={true} className={'mrg-bottom-20'}/>
                                        <FormControlLabelComponent label={'Primary Email :'}/>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'primary_email'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                id={'email_primary_input'}
                                                                label={'Email (Primary)'}
                                                                placeholder={'Email Address'}
                                                                type={"email"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1 d-flex align-items-center">
                                                <IconButtonComponent className={"form-helper-icon"}>
                                                    <ToolTipComponent
                                                        showArrow={true}
                                                        position={"right"}
                                                        tooltip={"This email address will be used to contact you if necessary. Please ensure it remains active and operational at all times."}>
                                                        <ImageConfig.InfoIcon/>
                                                    </ToolTipComponent>
                                                </IconButtonComponent>
                                            </div>
                                            <div className="ts-col"/>
                                        </div>
                                        {/*<HorizontalLineComponent className={'primary-phone-divider'}/>*/}
                                        {
                                            values?.secondary_emails && values?.secondary_emails?.length > 0 ?
                                                <>
                                                    <FormControlLabelComponent label={'Alternate Email Address'}/>
                                                    <FieldArray
                                                        name="secondary_emails"
                                                        render={(arrayHelpers) => (
                                                            <>
                                                                {values?.secondary_emails && values?.secondary_emails?.map((item: any, index: any) => {
                                                                    return (
                                                                        <div className="ts-row" key={index}>
                                                                            <div className="ts-col">
                                                                                <Field
                                                                                    name={`secondary_emails[${index}].email`}>
                                                                                    {
                                                                                        (field: FieldProps) => (
                                                                                            <FormikInputComponent
                                                                                                label={'Email Address'}
                                                                                                placeholder={'Enter Email Address'}
                                                                                                type={"email"}
                                                                                                formikField={field}
                                                                                                fullWidth={true}
                                                                                            />
                                                                                        )
                                                                                    }
                                                                                </Field>
                                                                            </div>
                                                                            <div
                                                                                className="ts-col-md-1 d-flex align-items-center registration-form-icons-wrapper">

                                                                                <IconButtonComponent
                                                                                    className={"form-helper-icon"}
                                                                                    onClick={() => {
                                                                                        arrayHelpers.remove(index);
                                                                                    }}
                                                                                >
                                                                                    <ImageConfig.DeleteIcon/>
                                                                                </IconButtonComponent>

                                                                                {values?.secondary_emails && (index === values?.secondary_emails?.length - 1) &&
                                                                                    values.secondary_emails.length < 3 &&
                                                                                    <IconButtonComponent
                                                                                        className={"form-helper-icon"}
                                                                                        disabled={!primaryEmailInfoSchema.isValidSync(values.secondary_emails[index])}
                                                                                        onClick={() => {
                                                                                            arrayHelpers.push(EmailObj);
                                                                                        }}
                                                                                    >
                                                                                        <ImageConfig.AddCircleIcon/>
                                                                                    </IconButtonComponent>
                                                                                }
                                                                            </div>
                                                                            <div className="ts-col"></div>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </>
                                                        )}/>
                                                </> :
                                                <ButtonComponent variant={"link"}
                                                                 color={"info"}
                                                                 size={"small"}
                                                                 className={'mrg-bottom-20'}
                                                                 prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                                 onClick={() => {
                                                                     setFieldValue('secondary_emails', [{
                                                                         email: ""
                                                                     }]);
                                                                 }}
                                                >
                                                    Add Alternate Email
                                                </ButtonComponent>
                                        }
                                    </DetailsViewCardComponent>
                                    <DetailsViewCardComponent title={"Address Information"}>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'address.address_line'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Address Line'}
                                                                placeholder={'Address Line'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'address.city'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'City'}
                                                                placeholder={'City'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"/>
                                        </div>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'address.state'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'State'}
                                                                placeholder={'State'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'address.zip_code'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Zip Code'}
                                                                validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                                placeholder={'Zip Code'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'address.country'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Country'}
                                                                placeholder={'Country'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                    </DetailsViewCardComponent>
                                    <DetailsViewCardComponent title={"Emergency Contact Information"}>
                                        <FormControlLabelComponent label={"Primary Emergency Contact"} size={'md'}/>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'emergency_contact_info.primary_emergency.name'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                id={'full_name_input'}
                                                                label={'Full Name'}
                                                                placeholder={'Enter Full Name'}
                                                                type={"text"}
                                                                required={true}
                                                                titleCase={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'emergency_contact_info.primary_emergency.relationship'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={'relationship_dd'}
                                                                options={relationshipList}
                                                                label={'Relationship'}
                                                                placeholder={'Select Relationship'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                isClearable={false}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'emergency_contact_info.primary_emergency.language'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={'language_dd'}
                                                                options={languageList}
                                                                label={'Language'}
                                                                placeholder={'Select Language'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                isClearable={false}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                        <FormControlLabelComponent label={'Primary Phone :'}/>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field
                                                    name={'emergency_contact_info.primary_emergency.primary_contact_info.phone_type'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={"p_phone_type"}
                                                                options={phoneTypeList}
                                                                label={'Phone Type'}
                                                                required={true}
                                                                placeholder={'Select Phone Type'}
                                                                formikField={field}
                                                                fullWidth={true}
                                                                isClearable={false}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field
                                                    name={'emergency_contact_info.primary_emergency.primary_contact_info.phone'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikPhoneInputComponent
                                                                id={"p_phone_number"}
                                                                label={'Phone Number'}
                                                                disabled={!values?.emergency_contact_info?.primary_emergency?.primary_contact_info?.phone_type}
                                                                placeholder={'Enter Phone Number'}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1 d-flex align-items-center">
                                                <IconButtonComponent className={"form-helper-icon"}>
                                                    <ToolTipComponent
                                                        showArrow={true}
                                                        position={"left"}
                                                        tooltip={"This phone number will be used to communicate with you in case of emergency. Ensure that this number is constantly operational."}>
                                                        <ImageConfig.InfoIcon/>
                                                    </ToolTipComponent>
                                                </IconButtonComponent>
                                            </div>

                                        </div>
                                        {/*<HorizontalLineComponent marginBottom={true}/>*/}
                                        <FieldArray
                                            name="emergency_contact_info.primary_emergency.secondary_contact_info"
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values?.emergency_contact_info?.primary_emergency?.secondary_contact_info && values?.emergency_contact_info?.primary_emergency?.secondary_contact_info?.length > 0 ?
                                                        <>
                                                            <FormControlLabelComponent label={'Alternate Phone'}
                                                            />
                                                            {values?.emergency_contact_info?.primary_emergency?.secondary_contact_info?.map((item: any, index: any) => {
                                                                    // @ts-ignore
                                                                    return (
                                                                        <div key={index}>

                                                                            <div className="ts-row">
                                                                                <div className="ts-col">
                                                                                    <Field
                                                                                        name={`emergency_contact_info.primary_emergency.secondary_contact_info[${index}].phone_type`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikSelectDropdownComponent
                                                                                                    options={phoneTypeList}
                                                                                                    label={'Phone Type'}
                                                                                                    placeholder={'Select Phone Type'}
                                                                                                    formikField={field}
                                                                                                    fullWidth={true}
                                                                                                    isClearable={false}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                                <div className="ts-col">
                                                                                    <Field
                                                                                        name={`emergency_contact_info.primary_emergency.secondary_contact_info[${index}].phone`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikPhoneInputComponent
                                                                                                    label={'Phone Number'}
                                                                                                    disabled={values.emergency_contact_info.primary_emergency.secondary_contact_info && !values.emergency_contact_info.primary_emergency.secondary_contact_info[index].phone_type}
                                                                                                    placeholder={'Enter Phone Number'}
                                                                                                    formikField={field}
                                                                                                    fullWidth={true}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                                <div
                                                                                    className="ts-col-md-1 d-flex align-items-center registration-form-icons-wrapper">

                                                                                    <IconButtonComponent
                                                                                        className={"form-helper-icon"}
                                                                                        onClick={() => {
                                                                                            arrayHelpers.remove(index);
                                                                                        }}
                                                                                    >
                                                                                        <ImageConfig.DeleteIcon/>
                                                                                    </IconButtonComponent>
                                                                                    {values?.emergency_contact_info?.primary_emergency?.secondary_contact_info && (index === values?.emergency_contact_info?.primary_emergency?.secondary_contact_info?.length - 1) &&
                                                                                        values.emergency_contact_info.primary_emergency.secondary_contact_info.length < 3 &&
                                                                                        <IconButtonComponent
                                                                                            className={"form-helper-icon"}
                                                                                            disabled={!contactInfoSchema.isValidSync(values?.emergency_contact_info?.primary_emergency?.secondary_contact_info && values?.emergency_contact_info?.primary_emergency?.secondary_contact_info[index])}
                                                                                            onClick={() => {
                                                                                                arrayHelpers.push({
                                                                                                    phone_type: "",
                                                                                                    phone: ""
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <ImageConfig.AddCircleIcon/>
                                                                                        </IconButtonComponent>
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            )}</>
                                                        :
                                                        <ButtonComponent variant={"link"}
                                                                         color={"info"}
                                                                         size={"small"}
                                                                         prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                                         onClick={() => {
                                                                             setFieldValue('emergency_contact_info.primary_emergency.secondary_contact_info', [{
                                                                                 phone_type: "",
                                                                                 phone: ""
                                                                             }]);
                                                                         }}
                                                        >
                                                            Add Alternate Phone
                                                        </ButtonComponent>}
                                                </>
                                            )}/>
                                        <HorizontalLineComponent marginBottom={true}/>
                                        {
                                            !values.show_secondary_emergency_form && <div>
                                                <ButtonComponent variant={"link"}
                                                                 color={"info"}
                                                                 className={'add-another-contact'}
                                                                 prefixIcon={<ImageConfig.AddCircleFilled/>}
                                                                 onClick={() => {
                                                                     setFieldValue('show_secondary_emergency_form', true);
                                                                 }}
                                                >
                                                    Add Another Contact
                                                </ButtonComponent>
                                            </div>
                                        }

                                        <>
                                            {values.show_secondary_emergency_form && <>
                                                <div
                                                    className={'d-flex ts-align-items-center ts-justify-content-between'}>
                                                    <FormControlLabelComponent label={"Secondary Emergency Contact"}/>
                                                    <ButtonComponent className={'remove-contact-button'}
                                                                     variant={'outlined'}
                                                                     color={'error'}
                                                                     onClick={() => {
                                                                         setFieldValue('show_secondary_emergency_form', false);
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.name', "");
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.relationship', "");
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.language', "");
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.primary_contact_info.phone_type', "");
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.primary_contact_info.phone', "");
                                                                         setFieldValue('emergency_contact_info.secondary_emergency.secondary_contact_info', [{
                                                                             phone_type: "",
                                                                             phone: ""
                                                                         }]);
                                                                     }}
                                                    >
                                                        Remove
                                                        Contact</ButtonComponent>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col">
                                                        <Field name={'emergency_contact_info.secondary_emergency.name'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'Full Name'}
                                                                        placeholder={'Full Name'}
                                                                        type={"text"}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col">
                                                        <Field
                                                            name={'emergency_contact_info.secondary_emergency.relationship'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikSelectDropdownComponent
                                                                        options={relationshipList}
                                                                        label={'Relationship'}
                                                                        placeholder={'Select Relationship'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        isClearable={false}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col-1"></div>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col">
                                                        <Field
                                                            name={'emergency_contact_info.secondary_emergency.language'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikSelectDropdownComponent
                                                                        options={languageList}
                                                                        label={'Language'}
                                                                        placeholder={'Select Language'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        isClearable={false}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col">
                                                    </div>
                                                    <div className="ts-col-1"></div>
                                                </div>
                                                <FormControlLabelComponent label={'Primary Phone'}/>
                                                <div className="ts-row">
                                                    <div className="ts-col">
                                                        <Field
                                                            name={'emergency_contact_info.secondary_emergency.primary_contact_info.phone_type'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikSelectDropdownComponent
                                                                        options={phoneTypeList}
                                                                        label={'Phone Type'}
                                                                        placeholder={'Select Phone Type'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        isClearable={false}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col">
                                                        <Field
                                                            name={'emergency_contact_info.secondary_emergency.primary_contact_info.phone'}>
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikPhoneInputComponent
                                                                        label={'Phone Number'}
                                                                        disabled={!values?.emergency_contact_info?.secondary_emergency?.primary_contact_info?.phone_type}
                                                                        placeholder={'Enter Phone Number (Primary)'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col-1 d-flex ts-align-items-center">
                                                        <IconButtonComponent className={"form-helper-icon"}>
                                                            <ToolTipComponent
                                                                showArrow={true}
                                                                position={"left"}
                                                                tooltip={"This phone number will be used as the primary number for your account. Please ensure that this number is constantly operational."}>
                                                                <ImageConfig.InfoIcon/>
                                                            </ToolTipComponent>
                                                        </IconButtonComponent>
                                                    </div>
                                                </div>
                                                <FieldArray
                                                    name="emergency_contact_info.secondary_emergency.secondary_contact_info"
                                                    render={(arrayHelpers) => (
                                                        <>
                                                            {values?.emergency_contact_info?.secondary_emergency?.secondary_contact_info &&  values?.emergency_contact_info?.secondary_emergency?.secondary_contact_info?.length > 0 ? <>
                                                                    <FormControlLabelComponent
                                                                        label={'Alternate Phone'}/>
                                                                    {values?.emergency_contact_info?.secondary_emergency?.secondary_contact_info?.map((item: any, index: any) => {
                                                                        return (
                                                                            <div className="ts-row" key={index}>
                                                                                <div className="ts-col">
                                                                                    <Field
                                                                                        name={`emergency_contact_info.secondary_emergency.secondary_contact_info[${index}].phone_type`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikSelectDropdownComponent
                                                                                                    options={phoneTypeList}
                                                                                                    label={'Phone Type'}
                                                                                                    placeholder={'Select Phone Type'}
                                                                                                    formikField={field}
                                                                                                    fullWidth={true}
                                                                                                    isClearable={false}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                                <div
                                                                                    className="ts-col">
                                                                                    <Field
                                                                                        name={`emergency_contact_info.secondary_emergency.secondary_contact_info[${index}].phone`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikPhoneInputComponent
                                                                                                    label={'Phone Number'}
                                                                                                    disabled={values.emergency_contact_info.secondary_emergency.secondary_contact_info && !values.emergency_contact_info.secondary_emergency.secondary_contact_info[index].phone_type}
                                                                                                    placeholder={'Enter Phone Number'}
                                                                                                    formikField={field}
                                                                                                    fullWidth={true}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                                <div
                                                                                    className="ts-col-md-1 d-flex align-items-center registration-form-icons-wrapper">

                                                                                        <IconButtonComponent
                                                                                            className={"form-helper-icon"}
                                                                                            onClick={() => {
                                                                                                arrayHelpers.remove(index);
                                                                                            }}
                                                                                        >
                                                                                            <ImageConfig.DeleteIcon/>
                                                                                        </IconButtonComponent>
                                                                                    { values.emergency_contact_info?.secondary_emergency?.secondary_contact_info && (index === values.emergency_contact_info?.secondary_emergency?.secondary_contact_info?.length - 1) &&
                                                                                        values.emergency_contact_info.secondary_emergency.secondary_contact_info.length < 3 &&
                                                                                        <IconButtonComponent
                                                                                            className={"form-helper-icon"}
                                                                                            disabled={!contactInfoSchema.isValidSync(values?.emergency_contact_info.secondary_emergency.secondary_contact_info && values?.emergency_contact_info.secondary_emergency.secondary_contact_info[index])}
                                                                                            onClick={() => {
                                                                                                arrayHelpers.push({
                                                                                                    phone_type: undefined,
                                                                                                    phone: undefined
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            <ImageConfig.AddCircleIcon/>
                                                                                        </IconButtonComponent>
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    })}
                                                                </> :
                                                                <ButtonComponent variant={"link"}
                                                                                 color={"info"}
                                                                                 size={"small"}
                                                                                 prefixIcon={
                                                                                     <ImageConfig.AddCircleFilled/>}
                                                                                 onClick={() => {
                                                                                     setFieldValue('emergency_contact_info.secondary_emergency.secondary_contact_info', [{
                                                                                         phone_type: undefined,
                                                                                         phone: undefined
                                                                                     }]);
                                                                                 }}
                                                                >
                                                                    Add Alternate Phone
                                                                </ButtonComponent>
                                                            }
                                                        </>
                                                    )
                                                    }/>
                                            </>
                                            }
                                        </>
                                    </DetailsViewCardComponent>
                                    <DetailsViewCardComponent title={"Work Information"}>
                                        <div className="ts-row">
                                            <div className="ts-col">
                                                <Field name={'work_info.occupation'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Occupation'}
                                                                placeholder={'Occupation'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col">
                                                <Field name={'work_info.employment_status'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                id={'employment_status_dd'}
                                                                label={'Employment Status'}
                                                                placeholder={'Select Employment Status'}
                                                                options={employmentStatusList}
                                                                required={true}
                                                                isClearable={false}
                                                                formikField={field}
                                                                fullWidth={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                            <div className="ts-col-1"></div>
                                        </div>
                                    </DetailsViewCardComponent>
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={"cancel_btn"}
                                            variant={"outlined"}
                                            className={'mrg-right-15'}
                                            disabled={isClientBasicDetailsSavingInProgress}
                                            onClick={onCancel}
                                            color={'accent'}
                                        >
                                            Cancel
                                        </ButtonComponent>
                                        <ButtonComponent
                                            id={"save_btn"}
                                            isLoading={isClientBasicDetailsSavingInProgress}
                                            disabled={isClientBasicDetailsSavingInProgress || !isValid}
                                            type={"submit"}
                                            color={'accent'}
                                        >
                                            {isClientBasicDetailsSavingInProgress ? "Saving" : "Save"}
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </CardComponent>
            <ModalComponent isOpen={isAcknowledgementModalOpen}>
                <AcknowledgementFormComponent setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
                                              isLoading={isClientBasicDetailsSavingInProgress}
                                              onSubmit={handleAcknowledgementFormSubmit}/>
            </ModalComponent>
        </div>
    );

};

export default ClientBasicDetailsFormComponent;
